import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Users, Settings, ArrowLeftRight, Activity } from "lucide-react";
import { useTelegramViewportBottomOffset } from "@/hooks/useTelegramViewportBottomOffset";

export type NavTab =
  | "alerts"
  | "trade"
  | "referrals"
  | "settings"
  | "price-alerts";

interface ReferralBottomNavProps {
  activeTab?: NavTab;
}

export const ReferralBottomNav: React.FC<ReferralBottomNavProps> = ({
  activeTab: activeTabProp,
}) => {
  const location = useLocation();

  const navigate = useNavigate();

  const onTabChange = (tab: NavTab) => {
    if (tab === "alerts") {
      navigate("/alerts");
    } else if (tab === "trade") {
      navigate("/positions");
    } else if (tab === "settings") {
      navigate("/settings");
    } else if (tab === "price-alerts") {
      navigate("/price-alerts");
    } else {
      navigate("/referrals");
    }
  };

  // Determine active tab from route if not provided
  const getActiveTab = (): NavTab => {
    if (activeTabProp) return activeTabProp;
    if (location.pathname.includes("/alerts")) return "alerts";
    if (location.pathname.includes("/positions")) return "trade";
    if (location.pathname.includes("/settings")) return "settings";
    if (location.pathname.includes("/price-alerts")) return "price-alerts";
    return "referrals";
  };

  const activeTab = getActiveTab();
  const viewportBottomOffset = useTelegramViewportBottomOffset();

  const safeBottom =
    "calc(var(--app-safe-bottom, var(--tg-viewport-safe-area-inset-bottom, var(--tg-safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))) + var(--tg-viewport-content-safe-area-inset-bottom, var(--tg-content-safe-area-inset-bottom, 0px)))";

  return (
    <>
      <div
        aria-hidden
        className="shrink-0"
        style={{ height: `calc(4rem + ${safeBottom})` }}
      />
      <nav
        className="fixed left-0 right-0 z-50 border-t border-border rounded-t-xl bg-tertiary"
        style={{
          bottom: `${viewportBottomOffset}px`,
          paddingBottom: safeBottom,
        }}
      >
        <div className="w-full flex items-center justify-around h-16">
          <button
            onClick={() => onTabChange("alerts")}
            className={`flex-1 flex flex-col items-center justify-center h-full transition-colors ${
              activeTab === "alerts"
                ? "text-accent"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <Bell className="w-5 h-5 mb-1" />
            <span className="text-xs ">Alerts</span>
          </button>

          <button
            onClick={() => onTabChange("trade")}
            className={`flex-1 flex flex-col items-center justify-center h-full transition-colors ${
              activeTab === "trade"
                ? "text-accent"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <ArrowLeftRight className="w-5 h-5 mb-1" />
            <span className="text-xs ">Trade</span>
          </button>

          <button
            onClick={() => onTabChange("referrals")}
            className={`flex-1 flex flex-col items-center justify-center h-full transition-colors ${
              activeTab === "referrals"
                ? "text-accent"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <Users className="w-5 h-5 mb-1" />
            <span className="text-xs ">Referrals</span>
          </button>

          <button
            onClick={() => onTabChange("price-alerts")}
            className={`flex-1 flex flex-col items-center justify-center h-full transition-colors ${
              activeTab === "price-alerts"
                ? "text-accent"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <Activity className="w-5 h-5 mb-1" />
            <span className="text-xs ">Price Alerts</span>
          </button>

          <button
            onClick={() => onTabChange("settings")}
            className={`flex-1 flex flex-col items-center justify-center h-full transition-colors ${
              activeTab === "settings"
                ? "text-accent"
                : "text-text-secondary hover:text-text-primary"
            }`}
          >
            <Settings className="w-5 h-5 mb-1" />
            <span className="text-xs ">Settings</span>
          </button>
        </div>
      </nav>
    </>
  );
};
